import type { CartItem, StoreSettings } from '../types';

// Mirrors the backend `computeOrderTotals` (see backend/src/lib/totals.ts) so the
// cart/checkout preview matches exactly what the server will charge. All money is
// integer cents USD. Pricing is NOT hardcoded here — it must be passed in from
// the live store settings fetched via GET /api/orders/pricing-config, keeping the
// backend the single source of truth. The subtotal (which needs no pricing rules)
// can be computed independently with `computeSubtotalCentsUSD`.
export interface CartTotals {
  subtotalCentsUSD: number;
  shippingCentsUSD: number;
  taxCentsUSD: number;
  totalCentsUSD: number;
}

// Line-item subtotal — the only figure that doesn't depend on store pricing, so
// it can be shown immediately while shipping/tax load from the backend.
export function computeSubtotalCentsUSD(items: CartItem[]): number {
  return items.reduce((sum, i) => sum + i.product.priceCentsUSD * i.quantity, 0);
}

// Each item's per-unit shipping is its own override, or the store default when
// unset (null). Free shipping waives it all above the threshold.
export function computeCartTotals(items: CartItem[], pricing: StoreSettings): CartTotals {
  const subtotalCentsUSD = items.reduce((sum, i) => sum + i.product.priceCentsUSD * i.quantity, 0);
  const shippingBeforeFreeCentsUSD = items.reduce((sum, i) => {
    const perUnit = i.product.shippingCentsUSD ?? pricing.defaultItemShippingCentsUSD;
    return sum + Math.max(0, perUnit) * i.quantity;
  }, 0);

  const threshold = pricing.freeShippingThresholdCentsUSD;
  const qualifiesForFreeShipping = threshold != null && subtotalCentsUSD >= threshold;
  const shippingCentsUSD = qualifiesForFreeShipping ? 0 : shippingBeforeFreeCentsUSD;

  const taxCentsUSD = Math.round(subtotalCentsUSD * pricing.salesTaxRate);
  const totalCentsUSD = subtotalCentsUSD + shippingCentsUSD + taxCentsUSD;
  return { subtotalCentsUSD, shippingCentsUSD, taxCentsUSD, totalCentsUSD };
}
